/**
 * Warmup step plan — pure music arithmetic, no audio, no React, no Firestore.
 *
 * Everything the sampler engine (`warmupSampler.js`) needs to decide WHAT to
 * play and WHEN, in beats, so it can be tested with a fake clock and the engine
 * is left with nothing but "play this buffer at that time".
 *
 * Two things are ported from the generator (`scripts/warmups/generate.py`) on
 * purpose, so the site sounds like the Drive files:
 *   - `repBars`  — the bar layout of one repetition (2-beat cue + padded pattern)
 *   - `repEvents` — the accompaniment figure: the I-chord cue in the new key,
 *     the I chord sustained under the pattern, and on patterns of three beats or
 *     more a V7 → I turnaround on the last two beats; unpitched patterns get
 *     tonic clicks on every beat instead (the generator's "hiss" special).
 *
 * The WALK is the other half. A walk is the sequence of tonics an exercise is
 * repeated on: up one semitone per rep from the start tonic to a top, then —
 * unlike the generator, which always comes straight back down — it HOLDS at
 * the top until the teacher taps Descend, Drop octave or Next (Anthony's
 * chosen default, 2026-09-03, "with the understanding this might change").
 * `advanceWalk` is a reducer over that state; `transposeWalk` / `dropOctave` /
 * `descend` are the teacher's taps. All pure.
 */
import { DEG } from '../pages/EmployeePortal/Warmups/warmupShape';

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** MIDI number → "C4". Sharps spelled with '#'. */
export const midiToName = (midi) => `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

/** "C4" | "Cs4" | "F#3" | "Bb3" → MIDI number. */
export const nameToMidi = (name) => {
	const m = /^([A-Ga-g])(#|s|b)?(-?\d)$/.exec(String(name).trim());
	if (!m) throw new Error(`Bad pitch name: ${name}`);
	const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[m[1].toUpperCase()];
	const alter = m[2] === '#' || m[2] === 's' ? 1 : m[2] === 'b' ? -1 : 0;
	return (Number(m[3]) + 1) * 12 + base + alter;
};

/** Pattern token → semitones above the tonic, or null for a rest ('R') / tick ('X'). */
export const tokenSemis = (tok) => {
	if (tok === 'R' || tok === 'X' || tok === 'H') return null;
	const v = DEG[tok];
	if (v === undefined) throw new Error(`Unknown scale-degree token: ${tok}`);
	return v;
};

/** Lowest and highest pitched token of a pattern, in semitones above the tonic. */
export const patternRange = (pattern) => {
	const semis = pattern.map((n) => tokenSemis(n[0])).filter((s) => s !== null);
	if (!semis.length) return { lo: 0, hi: 0 };
	return { lo: Math.min(...semis), hi: Math.max(...semis) };
};

export const isUnpitched = (pattern) => pattern.every((n) => n[0] === 'X' || n[0] === 'R' || n[0] === 'H');

export const patternBeats = (pattern) => pattern.reduce((a, n) => a + Number(n[1] || 0), 0);

const paddedBeats = (pattern) => Math.ceil(patternBeats(pattern) - 1e-9);

/** Beats per bar the player lays a rep out in. 4/4 by default; the teacher can switch. */
export const METERS = [4, 3, 2];
export const DEFAULT_METER = 4;
export const isMeter = (n) => METERS.includes(n);

/**
 * Bar lengths (in beats) for one repetition. Every rep is a WHOLE NUMBER OF
 * BARS in the chosen meter: the 2-beat cue, the pattern padded to whole
 * beats, then a rest that fills the last bar — the final chord sustains
 * through it (repEvents), so nothing sounds empty and the singer gets a
 * breath before the next key's cue.
 *
 * 2026-09-06: until now this was the generator's `rep_bars` verbatim — cue +
 * pattern in ONE bar when it fit, so a five-note pattern was a bar of 7/4 and
 * a three-note one 5/4 — which in the room read as "we're missing a beat"
 * (Anthony + Grace, live). The Python generator still lays its MP3s out the
 * old way; the site's meter belongs to the player, not the library, and
 * `matrix.score.maxSingleMeasureBeats` is now the generator's alone.
 */
export const repBars = (pattern, matrix, meter = DEFAULT_METER) => {
	const m = isMeter(meter) ? meter : DEFAULT_METER;
	const cue = matrix.score.cueBeats;
	const P = paddedBeats(pattern);
	const total = Math.max(m, Math.ceil((cue + P) / m) * m);
	return Array(total / m).fill(m);
};

export const repBeats = (pattern, matrix, meter = DEFAULT_METER) => repBars(pattern, matrix, meter).reduce((a, b) => a + b, 0);

/** Accompaniment styles. 'simple' is the generator's figure below; 'full' is
 * Andy's, transcribed from the octaves exercise of a rehearsal-room warmup
 * (2026-09-08, Anthony: "the octaves should go in right away"). Session-level,
 * like the meter — a player setting, never saved per student. */
export const ACCOMPANIMENTS = ['simple', 'full'];
export const DEFAULT_ACCOMPANIMENT = 'simple';
export const isAccompaniment = (s) => ACCOMPANIMENTS.includes(s);

/** I and V7 chords on a tonic, as MIDI — same voicing as the generator. */
export const iChord = (tonic) => [tonic, tonic + 4, tonic + 7];
export const v7Chord = (tonic) => [tonic + 7 - 12, tonic + 11 - 12, tonic + 2, tonic + 5];

/**
 * Every event of ONE repetition at one tonic, in beats from the rep start.
 *
 * guide: the pattern itself — `{ at, dur, midi, glideTo, staccato }`, plus the
 *        call notes (5-3-1) on the first three rests of an echo exercise, and
 *        `{ at, tick: true }` for each 'X'.
 * piano: the accompaniment — `{ at, dur, midis, staccato }`.
 *
 * `dur` is the sounding length: 95% of the written value, or the staccato
 * shortening the prototype used (≤ 45%, capped at 0.18 s expressed in beats
 * by the caller — here we keep beats and let the engine cap in seconds).
 */
/**
 * The FULL figure — what Andy plays under a climbing exercise, read off the
 * cleaned transcription (Drive: warmups/AndyWarmUp/cleaned-2026-09-08, ex02):
 *   - the cue is I with the root two octaves down and a close chord an octave
 *     below the singer, ROLLED low to high (the sampler staggers `roll` chords);
 *   - under the pattern the BASS WALKS 2 · 3 · 4 · 5 on the strong beats while
 *     the right hand keeps the tonic on top and moves the inner voices
 *     (5-and-1 over 2, I over 3, IV over 4, I over 5);
 *   - a pulse on the 5th on the weak beats, skipped where the guide is a
 *     semitone or a tone away from it;
 *   - the generator's V7 → I cadence on the last two pattern beats, voiced low;
 *   - and the part that makes the climb feel led: the last beats of the rep
 *     are the V7 OF THE NEXT KEY, so the new tonic arrives as a resolution.
 *     That needs the next tonic, which the sampler reads off the walk. A tap
 *     that changes the walk inside the last two beats gets one cue to the key
 *     it would have been — the same window the lookahead already tolerates.
 * Staccato exercises get the walk and the pulse short; the cue stays full.
 * Unpitched patterns keep the clicks. A tonic below C3 plays the figure an
 * octave up so the bass stays on real samples (the set's floor is C2).
 */
const BASS_FLOOR = 36; // C2
const FULL_STEPS = [
	// [bass degree above the tonic, right-hand chord relative to the (possibly lifted) tonic]
	[0, [-12, -8, -5, 0]], // I
	[2, [-5, 0]], // 5 and 1 over the 2nd
	[4, [-12, -5, 0]], // I over 3
	[5, [-12, -7, -3, 0]], // IV
	[7, [-8, -5, 0]], // I over 5
];
const fullBass = (t, deg) => { let b = t - 24 + deg; while (b < BASS_FLOOR) b += 12; return b; };
const fullChord = (t, rel) => rel.map((r) => t + r);
const fullI = (t) => [fullBass(t, 0), ...fullChord(t, FULL_STEPS[0][1])];
const fullV7 = (t) => [fullBass(t, 7) - 12 >= BASS_FLOOR ? fullBass(t, 7) - 12 : fullBass(t, 7), t - 10, t - 5, t - 1, t + 5];
const fullStep = (t, i) => { const [deg, rel] = FULL_STEPS[i % FULL_STEPS.length]; return [fullBass(t, deg), ...fullChord(t, rel)]; };

const fullPiano = ({ tonic, nextTonic, cue, P, pad, total, meter, staccato, guide }) => {
	const t = tonic < 48 ? tonic + 12 : tonic;
	const piano = [{ at: 0, dur: cue, midis: fullI(t), staccato: false, roll: true }];
	const isStrong = (b) => b % meter === 0 || (meter === 4 && b % meter === 2);
	const cadence = P >= 3;
	const stepEnd = cadence ? cue + P - 2 : cue + P; // the walk runs up to the cadence
	const strong = []; for (let b = cue; b < stepEnd; b += 1) if (isStrong(b)) strong.push(b);
	strong.forEach((b, i) => { const end = strong[i + 1] ?? stepEnd; piano.push({ at: b, dur: end - b, midis: fullStep(t, i + 1), staccato }); });
	// the pulse on the 5th: weak beats inside the walk, unless the guide sits a semitone or a tone from it
	const fifth = t + 7;
	for (let b = cue; b < stepEnd; b += 1) {
		if (isStrong(b)) continue;
		const clash = guide.some((g) => g.midi != null && g.at <= b && g.at + g.dur > b && Math.abs(g.midi - fifth) >= 1 && Math.abs(g.midi - fifth) <= 2);
		if (!clash) piano.push({ at: b, dur: 1, midis: [fifth], staccato });
	}
	const modCue = nextTonic != null && pad >= 1 ? Math.min(2, pad) : 0;
	if (cadence) {
		piano.push({ at: cue + P - 2, dur: 1, midis: fullV7(t), staccato: false });
		piano.push({ at: cue + P - 1, dur: 1 + pad - modCue, midis: fullI(t), staccato: false });
	} else if (stepEnd < total - modCue) {
		piano.push({ at: stepEnd, dur: total - modCue - stepEnd, midis: fullI(t), staccato: false });
	}
	if (modCue) { const n = nextTonic < 48 ? nextTonic + 12 : nextTonic; piano.push({ at: total - modCue, dur: modCue, midis: fullV7(n), staccato: false }); }
	return piano.sort((x, y) => x.at - y.at);
};

export const repEvents = (exercise, tonic, matrix, meter = DEFAULT_METER, { accompaniment = DEFAULT_ACCOMPANIMENT, nextTonic = null } = {}) => {
	const cue = matrix.score.cueBeats;
	const pattern = exercise.pattern;
	const bars = repBars(pattern, matrix, meter);
	const total = bars.reduce((a, b) => a + b, 0);
	const P = paddedBeats(pattern);
	// The rest that fills the last bar; the closing chord sustains through it.
	const pad = total - cue - P;
	const glides = new Set((exercise.glides || []).map((g) => `${g[0]}-${g[1]}`));
	const staccato = exercise.artic === 'staccato';

	const guide = [];
	let at = cue;
	pattern.forEach((n, i) => {
		const dur = Number(n[1]);
		if (n[0] === 'R') {
			if (exercise.echo && i < 3) {
				const call = ['5', '3', '1'][i];
				guide.push({ at, dur: dur * 0.95, midi: tonic + DEG[call], glideTo: null, staccato: false });
			}
			at += dur;
			return;
		}
		if (n[0] === 'X' || n[0] === 'H') {
			for (let k = 0; k < dur; k += 1) guide.push({ at: at + k, tick: true });
			at += dur;
			return;
		}
		const midi = tonic + tokenSemis(n[0]);
		const nx = pattern[i + 1];
		const glideTo = nx && glides.has(`${i}-${i + 1}`) && tokenSemis(nx[0]) !== null ? tonic + tokenSemis(nx[0]) : null;
		guide.push({ at, dur: staccato ? Math.min(dur * 0.45, 0.6) : dur * 0.95, midi, glideTo, staccato });
		at += dur;
	});

	if (accompaniment === 'full' && !isUnpitched(pattern)) {
		return { beats: total, bars, guide, piano: fullPiano({ tonic, nextTonic, cue, P, pad, total, meter: isMeter(meter) ? meter : DEFAULT_METER, staccato, guide }) };
	}
	const piano = [{ at: 0, dur: cue, midis: iChord(tonic), staccato: false }];
	if (isUnpitched(pattern)) {
		for (let b = cue; b < total - 1e-9; b += 1) piano.push({ at: b, dur: 0.5, midis: [tonic], staccato: true });
	} else if (P >= 3) {
		piano.push({ at: cue, dur: P - 2, midis: iChord(tonic), staccato: false });
		piano.push({ at: cue + P - 2, dur: 1, midis: v7Chord(tonic), staccato: false });
		piano.push({ at: cue + P - 1, dur: 1 + pad, midis: iChord(tonic), staccato: false });
	} else {
		piano.push({ at: cue, dur: P + pad, midis: iChord(tonic), staccato: false });
	}

	return { beats: total, bars, guide, piano };
};

/** Clamp (lo, hi MIDI) for an age band. */
export const clampFor = (ageBand, matrix) => {
	const [lo, hi] = matrix.ages[ageBand].clamp;
	return { lo: nameToMidi(lo), hi: nameToMidi(hi) };
};

/**
 * The highest tonic an exercise may be sung on for an age band: the pattern's
 * top note must not pass the clamp. Never below the start tonic.
 */
export const topTonicFor = (exercise, startTonic, ageBand, matrix) => {
	const { hi } = patternRange(exercise.pattern);
	const clamp = clampFor(ageBand, matrix);
	return Math.max(startTonic, clamp.hi - hi);
};

// ---------------------------------------------------------------------------
// The walk
// ---------------------------------------------------------------------------

/**
 * How far past the planned top a walk keeps climbing before it holds. The
 * planned top (`top`) is a REFERENCE now, not a stop (Anthony, 2026-10-02:
 * "They should go higher and the teacher should decide when to stop them") —
 * every exercise was stopping at the level cap + age clamp, which is below
 * what most students have. The walk climbs a semitone per rep until the
 * teacher taps Descend; `limit` is only the runaway guard for a walk nobody is
 * attending to, an octave past the plan, and "One more" still goes past it.
 */
export const CLIMB_PAST = 12;

/** The tonic a walk holds at. Falls back to `top` for a walk built before `limit` existed. */
const limitOf = (w) => (w.limit == null ? w.top : w.limit);

/**
 * Start a walk. `top` is the planned top (from `topTonicFor`, or the routine's
 * planned span: start + span). `span` wins when both are given and it is
 * lower — the routine's plan is the pedagogy, the clamp is the band's ceiling.
 * Neither stops the climb any more (see CLIMB_PAST); a span of 0 still means
 * "one key, held" (a single rep, or an exercise planned on one key).
 */
export const startWalk = ({ startTonic, top = null, span = null, climbPast = CLIMB_PAST }) => {
	let ceiling = top == null ? startTonic + (span ?? 0) : top;
	if (span != null) ceiling = Math.min(ceiling, startTonic + span);
	const planned = Math.max(startTonic, ceiling);
	const held = span === 0;
	const limit = held ? planned : planned + climbPast;
	return {
		startTonic,
		top: planned,
		limit,
		tonic: startTonic,
		phase: held || limit <= startTonic ? 'hold' : 'up',
		rep: 0,
		octaveShift: 0,
		done: false,
	};
};

/**
 * The tonic for the NEXT repetition, applying the walk's phase:
 *   up   → one semitone higher, past the planned top, until the limit, where it HOLDS
 *   hold → the same tonic, until a tap
 *   down → one semitone lower, until the start tonic, then done
 */
export const advanceWalk = (w) => {
	if (w.done) return w;
	const rep = w.rep + 1;
	if (w.phase === 'up') {
		const next = w.tonic + 1;
		const limit = limitOf(w);
		if (next >= limit) return { ...w, tonic: limit, phase: 'hold', rep };
		return { ...w, tonic: next, rep };
	}
	if (w.phase === 'hold') return { ...w, rep };
	// down
	// transposeWalk / dropOctave already moved startTonic, so it IS the floor;
	// octaveShift is informational (the readout says "an octave down").
	const next = w.tonic - 1;
	const floor = w.startTonic;
	if (next <= floor) return { ...w, tonic: floor, rep, done: true };
	return { ...w, tonic: next, rep };
};

/** Teacher tap: come down from wherever we are. */
export const descend = (w) => ({ ...w, phase: 'down' });

/**
 * Teacher tap: Next. During the climb it is advanceWalk — the next key, now.
 * AT THE LIMIT it means "one more": Next raises the limit by one and goes
 * there. (Until 2026-10-02 the walk held at the planned top itself and this
 * was the only way past it; it now climbs past the plan on its own.) Coming down it is the
 * next key down. Before 2026-09-06 Next in the hold phase asked advanceWalk
 * for the next key, was told "the same one, we are holding", and RESTARTED
 * the same rep — which read in the room as "it plays again if you miss the
 * window" (Anthony + Grace, live). The window was exactly one rep wide: the
 * rep below the top, the only one where Next still climbed.
 */
export const nextKey = (w) => {
	if (w.done) return w;
	if (w.phase === 'hold') return { ...w, tonic: w.tonic + 1, limit: Math.max(limitOf(w), w.tonic + 1), rep: w.rep + 1 };
	return advanceWalk(w);
};

/**
 * Teacher tap: the rest of the walk continues n semitones from where it would
 * have (applies to the next rep; the current one finishes). Moves the top too,
 * so a downward transpose does not turn into an immediate hold.
 */
export const transposeWalk = (w, n) => ({
	...w,
	tonic: w.tonic + n,
	top: w.top + n,
	limit: limitOf(w) + n,
	startTonic: w.startTonic + n,
});

/** Teacher tap: continue an octave lower (or back up). Same shape as transpose. */
export const dropOctave = (w) => ({ ...transposeWalk(w, -12), octaveShift: w.octaveShift - 12 });
export const raiseOctave = (w) => ({ ...transposeWalk(w, 12), octaveShift: w.octaveShift + 12 });

/** Restart the walk from its start tonic (keeps any transposition). */
export const rewindWalk = (w) => ({ ...w, tonic: w.startTonic, phase: limitOf(w) > w.startTonic ? 'up' : 'hold', rep: 0, done: false });

/** True once the walk has climbed past its planned top (the readout says so). */
export const pastPlannedTop = (w) => w != null && w.tonic != null && w.top != null && w.phase !== 'down' && w.tonic > w.top;

/**
 * The generator's fixed walk — up to the span and straight back down, plus any
 * extra cool-down reps on the start tonic — used to ESTIMATE a routine's
 * length (the site's walk holds at the top, so its real length is up to the
 * teacher). Verbatim port of PlanItem.steps.
 */
export const plannedSteps = (span, extraReps = 0) => {
	let steps = span <= 0 ? [0] : [...Array(span + 1).keys(), ...[...Array(span).keys()].reverse()];
	if (extraReps > 0) steps = [...steps, ...Array(extraReps).fill(0)];
	else if (extraReps < 0) steps = steps.slice(0, Math.max(1, steps.length + extraReps));
	return steps;
};

/** Seconds for one exercise in a routine, generator-style: lead bar + reps × rep beats. */
export const exerciseSeconds = ({ pattern, bpm, span, extraReps = 0, first = false, meter = DEFAULT_METER }, matrix) => {
	const lead = first ? matrix.score.countInBeats : matrix.score.restBarBeats;
	const beats = lead + plannedSteps(span, extraReps).length * repBeats(pattern, matrix, meter);
	return (beats * 60) / bpm;
};
